import axiosClient from './axiosClient';

/**
 * DB-backed tenant-attribute definitions (config.tenant_attr_definition,
 * migration 280). These extend the hard-coded `tenantAttrSchema` so a new
 * attribute can be surfaced on the Tenant Attributes form without a deploy.
 *
 * Definitions are GLOBAL (one set per environment, shared by every tenant) and
 * are authored from the MasterTenant → Attributes → Attribute Definitions tab.
 */

export interface TenantAttrDefinition {
  id: number;
  /** Dot-path into the tenant_attr JSONB, e.g. `myFlag` or `ux.themeColors.accent9`. */
  attrKey: string;
  label: string;
  description: string | null;
  /** A SchemaForm FieldType value. Unknown values degrade to a JSON editor. */
  fieldType: string;
  /** Category (left nav in the attributes form). */
  group: string;
  subGroup: string | null;
  options: { label: string; value: string }[] | null;
  dependsOnKey: string | null;
  dependsOnValue: unknown;
  placeholder: string | null;
  required: boolean;
  displayOrder: number;
  active: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface TenantAttrDefinitionListResult {
  /** false when migration 280 has not run on this environment yet. */
  available: boolean;
  definitions: TenantAttrDefinition[];
}

export type TenantAttrDefinitionInput = Omit<
  TenantAttrDefinition,
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
>;

export const tenantAttrDefinitionApi = {
  /**
   * List definitions. `includeInactive` is for the management table; the
   * attributes form asks for active rows only.
   *
   * Never throws on a missing table — the server reports `available: false`.
   */
  list: async (includeInactive = false): Promise<TenantAttrDefinitionListResult> => {
    const { data } = await axiosClient.get(
      `/api/tenant-attr-definition${includeInactive ? '?includeInactive=true' : ''}`,
    );
    // Defensive: an older backend (no such route) 404s and axios throws, which
    // React Query surfaces as an error — callers treat that as "no definitions".
    return {
      available: data?.available !== false,
      definitions: Array.isArray(data?.definitions) ? data.definitions : [],
    };
  },

  create: async (payload: TenantAttrDefinitionInput): Promise<TenantAttrDefinition> => {
    const { data } = await axiosClient.post('/api/tenant-attr-definition', payload);
    return data;
  },

  update: async (id: number, payload: TenantAttrDefinitionInput): Promise<TenantAttrDefinition> => {
    const { data } = await axiosClient.put(`/api/tenant-attr-definition/${id}`, payload);
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await axiosClient.delete(`/api/tenant-attr-definition/${id}`);
  },
};

export default tenantAttrDefinitionApi;
